/**
 * chat — shared concierge-chat state for the web PWA.
 *
 * The hero chat in ClientWelcome and the persistent dock that follows the
 * client around the rest of the site need to be the *same* conversation —
 * otherwise saying "yes" in the dock to a question the hero just asked would
 * fall on the floor. We hold the thread + canned-reply logic here so both
 * surfaces can subscribe and the persistence lives in one place.
 *
 * Phase 0: thread is local-only (localStorage, see ./identity). Phase 3 swaps
 * the persistence + botReply for the voice-agent worker over voice-sdk.
 *
 * Because nothing typed here reaches anyone, every canned reply must describe
 * something the product really does, and none may promise that an agent will
 * read the conversation or follow up on it. No listings are shown: listing
 * search is not built, so any card would be invented (spec 024 removed the one
 * that was).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { loadChat, saveChat, type StoredChatMessage } from "./identity";

const TYPING_MS = 850;

export type ChatMessage = StoredChatMessage;
const GREETING: ChatMessage = {
  id: 0,
  role: "bot",
  text: "Hi! This preview answers a few common questions. Are you buying, selling, or looking for a mortgage?",
};

export const QUICK_REPLIES = [
  "What documents do I need?",
  "Book a showing",
  "Mortgage pre-approval",
] as const;

interface BotReply {
  text: string;
}

function botReply(input: string): BotReply {
  const t = input.toLowerCase();
  // Signing before documents: "do I sign any document?" is about signing.
  if (/(\bsign\b|signing|signature|e-sign|esign|agreement|contract)/.test(t)) {
    return {
      text: "Yes, buyers e-sign their buyer representation agreement in the app. It is sent to you once your agent approves your application.",
    };
  }
  if (/(secure|security|safe|privacy|private|stored|encrypt)/.test(t)) {
    return {
      text: "Your documents are stored encrypted, and you sign in with a passkey (Face ID or your fingerprint) instead of a password.",
    };
  }
  if (/(document|paperwork|upload|pay ?stub|licen[cs]e|\bid\b)/.test(t)) {
    return {
      text: "Once you create your account you get a checklist for your situation. Snap a photo of each document and we read it, file it, and tick it off.",
    };
  }
  if (
    /(mortgage|afford|loan|prequal|pre-qual|pre-approv|preapprov|financ)/.test(
      t,
    )
  ) {
    return {
      text: "If your loan officer offers it here, create your account, choose “Get Pre-Approved”, and fill in your loan application online.",
    };
  }
  if (/(showing|tour|visit)/.test(t)) {
    return {
      text: "After your buyer representation agreement is signed, request a showing in the app and your agent confirms the time.",
    };
  }
  if (/(sell|selling|valuation|list my)/.test(t)) {
    return {
      text: "Create your account and choose “Sell My Home” to start working with your agent on the sale.",
    };
  }
  if (/(find a home|buy|home|house|search|listing)/.test(t)) {
    return {
      text: "Create your account and choose “Buy a Home” — your agent works with you from there.",
    };
  }
  return {
    text: "This preview only answers a few common questions. Create your account to connect with your agent for anything else.",
  };
}

// ---- module-level store so all subscribers see the same thread --------------

type Listener = (messages: ChatMessage[], typing: boolean) => void;
type DockListener = (open: boolean) => void;

let messages: ChatMessage[] = [];
let typing = false;
let nextId = 1;
let initialized = false;
let dockOpen = false;
const listeners = new Set<Listener>();
const dockListeners = new Set<DockListener>();

function notify() {
  for (const fn of listeners) fn(messages, typing);
}

function notifyDock() {
  for (const fn of dockListeners) fn(dockOpen);
}

export function openDock(): void {
  if (dockOpen) return;
  dockOpen = true;
  notifyDock();
}

export function closeDock(): void {
  if (!dockOpen) return;
  dockOpen = false;
  notifyDock();
}

export function useDockOpen(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(dockOpen);
  useEffect(() => {
    const fn: DockListener = (v) => setOpen(v);
    dockListeners.add(fn);
    setOpen(dockOpen);
    return () => {
      dockListeners.delete(fn);
    };
  }, []);
  return [
    open,
    (v: boolean) => {
      if (v) openDock();
      else closeDock();
    },
  ];
}

function init() {
  if (initialized) return;
  initialized = true;
  const persisted = loadChat();
  if (persisted && persisted.length > 0) {
    messages = persisted;
    nextId = Math.max(...persisted.map((m) => m.id), 0) + 1;
  } else {
    messages = [GREETING];
    nextId = 1;
  }
}

function persist() {
  saveChat(messages);
}

function sendMessage(text: string) {
  const trimmed = text.trim();
  if (!trimmed) return;
  messages = [...messages, { id: nextId++, role: "user", text: trimmed }];
  typing = true;
  persist();
  notify();
  window.setTimeout(() => {
    const reply = botReply(trimmed);
    messages = [...messages, { id: nextId++, role: "bot", text: reply.text }];
    typing = false;
    persist();
    notify();
  }, TYPING_MS);
}

export interface UseChatResult {
  messages: ChatMessage[];
  typing: boolean;
  send: (text: string) => void;
}

export function useChat(): UseChatResult {
  init();
  const [, force] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const fn: Listener = () => {
      if (mountedRef.current) force((n) => n + 1);
    };
    listeners.add(fn);
    return () => {
      mountedRef.current = false;
      listeners.delete(fn);
    };
  }, []);

  const send = useCallback((text: string) => sendMessage(text), []);

  return { messages, typing, send };
}
